﻿import { useState, useEffect } from 'react';
import { useAuthStore } from '../hooks/useAuthStore';
import { User, Heart, Pill, AlertTriangle, Syringe, Save, Plus, Trash2, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const API = import.meta.env.VITE_API_URL || '/api';

const TABS = [
  { id: 'demographics', label: 'Personal Info', icon: User },
  { id: 'conditions', label: 'Medical History', icon: Heart },
  { id: 'medications', label: 'Medications', icon: Pill },
  { id: 'allergies', label: 'Allergies', icon: AlertTriangle },
  { id: 'immunizations', label: 'Immunizations', icon: Syringe },
];

export default function PatientProfile() {
  const { user, token } = useAuthStore();
  const [activeTab, setActiveTab] = useState('demographics');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [credits, setCredits] = useState(0);

  const [profile, setProfile] = useState({
    date_of_birth: '', gender: '', phone: '', address: '', suburb: '',
    state: '', postcode: '', country: 'Australia', medicare_number: '',
    medicare_expiry: '', pension_number: '', emergency_contact_name: '',
    emergency_contact_phone: '', emergency_contact_relationship: ''
  });

  const [conditions, setConditions] = useState([]);
  const [medications, setMedications] = useState([]);
  const [allergies, setAllergies] = useState([]);
  const [immunizations, setImmunizations] = useState([]);

  // New item forms
  const [newCondition, setNewCondition] = useState({ condition_name: '', diagnosis_date: '', status: 'active', notes: '' });
  const [newMedication, setNewMedication] = useState({ medication_name: '', dose: '', frequency: '', prescribing_doctor: '', start_date: '', is_current: 1, notes: '' });
  const [newAllergy, setNewAllergy] = useState({ allergen: '', reaction: '', severity: 'mild', notes: '' });
  const [newImmunization, setNewImmunization] = useState({ vaccine_name: '', date_given: '', next_due: '', batch_number: '', notes: '' });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/patient/profile.php`, { headers }).then(r => r.json());
      if (res.success) {
        if (res.profile) setProfile(prev => ({ ...prev, ...res.profile }));
        setConditions(res.conditions || []);
        setMedications(res.medications || []);
        setAllergies(res.allergies || []);
        setImmunizations(res.immunizations || []);
        setCredits(res.credits || 0);
      }
    } catch (err) {
      console.error('Failed to load profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/patient/profile.php`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(profile),
      });
      toast.success('Profile saved successfully!');
    } catch (err) {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const addMedicalItem = async (type, data, resetFn) => {
    try {
      const res = await fetch(`${API}/patient/medical-history.php?type=${type}`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }).then(r => r.json());
      if (res.success) {
        toast.success('Added successfully');
        loadProfile();
        resetFn();
      }
    } catch (err) {
      toast.error('Failed to add item');
    }
  };

  const deleteMedicalItem = async (type, id) => {
    if (!confirm('Delete this entry?')) return;
    try {
      await fetch(`${API}/patient/medical-history.php?type=${type}&id=${id}`, { method: 'DELETE', headers });
      toast.success('Deleted');
      loadProfile();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const auStates = ['NSW','VIC','QLD','WA','SA','TAS','ACT','NT'];

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-purple-50">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-600">Loading your health profile...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 to-purple-50">
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">My Health Profile</h1>
            <p className="text-gray-500 mt-1">Your private medical information — only visible to you and your treating doctors</p>
          </div>
          <div className="bg-white rounded-xl px-5 py-3 shadow border border-purple-100 text-center">
            <p className="text-xs text-gray-500 mb-1">Available Credits</p>
            <p className="text-2xl font-bold text-purple-700">{credits.toLocaleString()}</p>
            <p className="text-xs text-gray-400">credits</p>
          </div>
        </div>

        {/* Privacy notice */}
        <div className="mb-4 bg-green-50 border border-green-200 rounded-lg px-4 py-3 flex items-center space-x-3">
          <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
          <p className="text-sm text-green-800">
            <strong>Private & Secure:</strong> Your health information is encrypted and only shared with doctors you book appointments with.
          </p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="flex border-b border-gray-200 overflow-x-auto">
            {TABS.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-5 py-4 text-sm font-medium whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'border-b-2 border-blue-600 text-blue-700 bg-purple-50'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          <div className="p-6">
            {/* DEMOGRAPHICS */}
            {activeTab === 'demographics' && (
              <div>
                <h2 className="text-lg font-bold text-gray-900 mb-6">Personal & Contact Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                    <input type="date" value={profile.date_of_birth || ''} onChange={e => setProfile({...profile, date_of_birth: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                    <select value={profile.gender || ''} onChange={e => setProfile({...profile, gender: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500">
                      <option value="">Select...</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                      <option value="prefer_not_to_say">Prefer not to say</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Phone</label>
                    <input type="tel" value={profile.phone || ''} onChange={e => setProfile({...profile, phone: e.target.value})}
                      placeholder="04xx xxx xxx" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                    <input type="text" value={profile.country || 'Australia'} onChange={e => setProfile({...profile, country: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                    <input type="text" value={profile.address || ''} onChange={e => setProfile({...profile, address: e.target.value})}
                      placeholder="123 Main Street" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Suburb/City</label>
                    <input type="text" value={profile.suburb || ''} onChange={e => setProfile({...profile, suburb: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                    <select value={profile.state || ''} onChange={e => setProfile({...profile, state: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500">
                      <option value="">Select state...</option>
                      {auStates.map(s => <option key={s} value={s}>{s}</option>)}
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                    <input type="text" value={profile.postcode || ''} onChange={e => setProfile({...profile, postcode: e.target.value})}
                      maxLength={4} placeholder="2000" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500" />
                  </div>
                </div>

                <h3 className="text-md font-bold text-gray-900 mt-8 mb-4">Medicare & Concession</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Medicare Number</label>
                    <input type="text" value={profile.medicare_number || ''} onChange={e => setProfile({...profile, medicare_number: e.target.value})}
                      placeholder="2xxx xxx x-1" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Medicare Expiry (MM/YYYY)</label>
                    <input type="text" value={profile.medicare_expiry || ''} onChange={e => setProfile({...profile, medicare_expiry: e.target.value})}
                      placeholder="12/2026" maxLength={7} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pension/DVA Number</label>
                    <input type="text" value={profile.pension_number || ''} onChange={e => setProfile({...profile, pension_number: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500" />
                  </div>
                </div>

                <h3 className="text-md font-bold text-gray-900 mt-8 mb-4">Emergency Contact</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    <input type="text" value={profile.emergency_contact_name || ''} onChange={e => setProfile({...profile, emergency_contact_name: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input type="tel" value={profile.emergency_contact_phone || ''} onChange={e => setProfile({...profile, emergency_contact_phone: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                    <select value={profile.emergency_contact_relationship || ''} onChange={e => setProfile({...profile, emergency_contact_relationship: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500">
                      <option value="">Select...</option>
                      <option>Spouse/Partner</option><option>Parent</option><option>Child</option>
                      <option>Sibling</option><option>Friend</option><option>Other</option>
                    </select>
                  </div>
                </div>

                <div className="mt-8 flex justify-end">
                  <button onClick={saveProfile} disabled={saving}
                    className="flex items-center space-x-2 bg-purple-700 hover:bg-purple-800 disabled:opacity-50 text-white font-bold py-3 px-8 rounded-xl transition-colors">
                    <Save className="w-5 h-5" />
                    <span>{saving ? 'Saving...' : 'Save Profile'}</span>
                  </button>
                </div>
              </div>
            )}

            {/* MEDICAL CONDITIONS */}
            {activeTab === 'conditions' && (
              <div>
                <h2 className="text-lg font-bold text-gray-900 mb-4">Past & Current Medical Conditions</h2>
                {conditions.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <Heart className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No conditions recorded yet</p>
                  </div>
                ) : (
                  <div className="space-y-3 mb-6">
                    {conditions.map(c => (
                      <div key={c.id} className="flex items-start justify-between p-4 bg-gray-50 rounded-lg border">
                        <div>
                          <p className="font-semibold text-gray-900">{c.condition_name}</p>
                          <div className="flex space-x-3 mt-1">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${c.status === 'active' ? 'bg-red-100 text-red-700' : c.status === 'monitoring' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>
                              {c.status}
                            </span>
                            {c.diagnosis_date && <span className="text-xs text-gray-500">Diagnosed: {c.diagnosis_date}</span>}
                          </div>
                          {c.notes && <p className="text-sm text-gray-500 mt-1">{c.notes}</p>}
                        </div>
                        <button onClick={() => deleteMedicalItem('conditions', c.id)} className="text-red-400 hover:text-red-600 ml-3">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="border-2 border-dashed border-purple-200 rounded-xl p-5">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2"><Plus className="w-4 h-4 text-purple-700" /><span>Add Condition</span></h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input placeholder="Condition name *" value={newCondition.condition_name} onChange={e => setNewCondition({...newCondition, condition_name: e.target.value})}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
                    <input type="date" placeholder="Diagnosis date" value={newCondition.diagnosis_date} onChange={e => setNewCondition({...newCondition, diagnosis_date: e.target.value})}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
                    <select value={newCondition.status} onChange={e => setNewCondition({...newCondition, status: e.target.value})}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500">
                      <option value="active">Active</option>
                      <option value="monitoring">Monitoring</option>
                      <option value="resolved">Resolved</option>
                    </select>
                    <input placeholder="Notes" value={newCondition.notes} onChange={e => setNewCondition({...newCondition, notes: e.target.value})}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 md:col-span-2" />
                    <button onClick={() => addMedicalItem('conditions', newCondition, () => setNewCondition({ condition_name: '', diagnosis_date: '', status: 'active', notes: '' }))}
                      disabled={!newCondition.condition_name}
                      className="bg-purple-700 hover:bg-purple-800 disabled:opacity-50 text-white font-medium py-2 px-4 rounded-lg text-sm">
                      Add
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* MEDICATIONS */}
            {activeTab === 'medications' && (
              <div>
                <h2 className="text-lg font-bold text-gray-900 mb-4">Current & Past Medications</h2>
                {medications.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <Pill className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No medications recorded yet</p>
                  </div>
                ) : (
                  <div className="space-y-3 mb-6">
                    {medications.map(m => (
                      <div key={m.id} className="flex items-start justify-between p-4 bg-gray-50 rounded-lg border">
                        <div>
                          <div className="flex items-center space-x-2">
                            <p className="font-semibold text-gray-900">{m.medication_name}</p>
                            {m.is_current ? <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Current</span> : <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Past</span>}
                          </div>
                          <p className="text-sm text-gray-500 mt-1">{[m.dose, m.frequency, m.prescribing_doctor && `Prescribed by ${m.prescribing_doctor}`].filter(Boolean).join(' · ')}</p>
                        </div>
                        <button onClick={() => deleteMedicalItem('medications', m.id)} className="text-red-400 hover:text-red-600 ml-3">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="border-2 border-dashed border-green-200 rounded-xl p-5">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2"><Plus className="w-4 h-4 text-green-600" /><span>Add Medication</span></h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input placeholder="Medication name *" value={newMedication.medication_name} onChange={e => setNewMedication({...newMedication, medication_name: e.target.value})}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                    <input placeholder="Dose (e.g. 10mg)" value={newMedication.dose} onChange={e => setNewMedication({...newMedication, dose: e.target.value})}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                    <input placeholder="Frequency (e.g. twice daily)" value={newMedication.frequency} onChange={e => setNewMedication({...newMedication, frequency: e.target.value})}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                    <input placeholder="Prescribing doctor" value={newMedication.prescribing_doctor} onChange={e => setNewMedication({...newMedication, prescribing_doctor: e.target.value})}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                    <select value={newMedication.is_current} onChange={e => setNewMedication({...newMedication, is_current: e.target.value})}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                      <option value={1}>Currently taking</option>
                      <option value={0}>Past medication</option>
                    </select>
                    <button onClick={() => addMedicalItem('medications', newMedication, () => setNewMedication({ medication_name: '', dose: '', frequency: '', prescribing_doctor: '', start_date: '', is_current: 1, notes: '' }))}
                      disabled={!newMedication.medication_name}
                      className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-medium py-2 px-4 rounded-lg text-sm">
                      Add
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ALLERGIES */}
            {activeTab === 'allergies' && (
              <div>
                <h2 className="text-lg font-bold text-gray-900 mb-4">Allergies & Adverse Reactions</h2>
                {allergies.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No allergies recorded</p>
                  </div>
                ) : (
                  <div className="space-y-3 mb-6">
                    {allergies.map(a => (
                      <div key={a.id} className={`flex items-start justify-between p-4 rounded-lg border ${a.severity === 'anaphylaxis' ? 'bg-red-50 border-red-300' : a.severity === 'severe' ? 'bg-orange-50 border-orange-200' : 'bg-yellow-50 border-yellow-200'}`}>
                        <div>
                          <div className="flex items-center space-x-2">
                            <p className="font-semibold text-gray-900">⚠️ {a.allergen}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${a.severity === 'anaphylaxis' ? 'bg-red-200 text-red-800' : a.severity === 'severe' ? 'bg-orange-200 text-orange-800' : a.severity === 'moderate' ? 'bg-yellow-200 text-yellow-800' : 'bg-gray-200 text-gray-700'}`}>
                              {a.severity}
                            </span>
                          </div>
                          {a.reaction && <p className="text-sm text-gray-600 mt-1">Reaction: {a.reaction}</p>}
                        </div>
                        <button onClick={() => deleteMedicalItem('allergies', a.id)} className="text-red-400 hover:text-red-600 ml-3">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="border-2 border-dashed border-orange-200 rounded-xl p-5">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2"><Plus className="w-4 h-4 text-orange-600" /><span>Add Allergy</span></h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <input placeholder="Allergen (e.g. Penicillin) *" value={newAllergy.allergen} onChange={e => setNewAllergy({...newAllergy, allergen: e.target.value})}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
                    <input placeholder="Reaction" value={newAllergy.reaction} onChange={e => setNewAllergy({...newAllergy, reaction: e.target.value})}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
                    <select value={newAllergy.severity} onChange={e => setNewAllergy({...newAllergy, severity: e.target.value})}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500">
                      <option value="mild">Mild</option>
                      <option value="moderate">Moderate</option>
                      <option value="severe">Severe</option>
                      <option value="anaphylaxis">Anaphylaxis</option>
                    </select>
                    <button onClick={() => addMedicalItem('allergies', newAllergy, () => setNewAllergy({ allergen: '', reaction: '', severity: 'mild', notes: '' }))}
                      disabled={!newAllergy.allergen}
                      className="bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white font-medium py-2 px-4 rounded-lg text-sm">
                      Add
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* IMMUNIZATIONS */}
            {activeTab === 'immunizations' && (
              <div>
                <h2 className="text-lg font-bold text-gray-900 mb-4">Immunization History</h2>
                {immunizations.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <Syringe className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No immunizations recorded yet</p>
                  </div>
                ) : (
                  <div className="space-y-3 mb-6">
                    {immunizations.map(imm => (
                      <div key={imm.id} className="flex items-start justify-between p-4 bg-purple-50 rounded-lg border border-purple-100">
                        <div>
                          <p className="font-semibold text-gray-900">💉 {imm.vaccine_name}</p>
                          <div className="flex space-x-4 mt-1">
                            {imm.date_given && <span className="text-xs text-gray-500">Given: {imm.date_given}</span>}
                            {imm.next_due && <span className="text-xs text-purple-700 font-medium">Next due: {imm.next_due}</span>}
                          </div>
                        </div>
                        <button onClick={() => deleteMedicalItem('immunizations', imm.id)} className="text-red-400 hover:text-red-600 ml-3">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="border-2 border-dashed border-purple-200 rounded-xl p-5">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2"><Plus className="w-4 h-4 text-purple-700" /><span>Add Immunization</span></h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <input placeholder="Vaccine name *" value={newImmunization.vaccine_name} onChange={e => setNewImmunization({...newImmunization, vaccine_name: e.target.value})}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Date given</label>
                      <input type="date" value={newImmunization.date_given} onChange={e => setNewImmunization({...newImmunization, date_given: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Next due</label>
                      <input type="date" value={newImmunization.next_due} onChange={e => setNewImmunization({...newImmunization, next_due: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
                    </div>
                    <button onClick={() => addMedicalItem('immunizations', newImmunization, () => setNewImmunization({ vaccine_name: '', date_given: '', next_due: '', batch_number: '', notes: '' }))}
                      disabled={!newImmunization.vaccine_name}
                      className="bg-purple-700 hover:bg-purple-800 disabled:opacity-50 text-white font-medium py-2 px-4 rounded-lg text-sm self-end">
                      Add
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
